import React, { useCallback, useEffect, useState, useContext } from "react";
import { BiPencil, BiPlus } from "react-icons/bi";
import { HiOutlineTrash } from "react-icons/hi";
import Breadcrumb from "../../../components/common/Breadcrumb/Breadcrumb";
import {
  SP_POLICIES_BREADCRUMB,
  SP_POLICIES_BREADCRUMB_PAGES,
} from "../../../Constants/BreadcrumbConstant";
import CircularLoader from "../../../components/common/CircularLoader/CircularLoader";
import Header from "../../../components/common/Header/Header";
import SideBar from "../../../components/common/SideBar/SideBarNew";
import { LoginContext } from "../../../helpers/LoginContext";
import {
  addFiltersAndPagination,
  apiGetRequest,
  apiPostRequest,
  apiPutRequest,
  apiDeleteRequest,
} from "../../../helpers/Requests";
import {
  getPaginationObj,
  normalizePolicyCategoriesIndexPayload,
} from "../../../Utils/utils";
import PaginationComponent from "../../../components/common/Pagination/index";
import {
  emptyPaginationObj,
  POLICY_CATEGORIES_LIST_LIMIT,
} from "../../../Constants/constant";
import { toast } from "react-toastify";
import Loader from "../../../components/common/Loader/Loader";
import { roleAccess } from "../../../helpers/RolesPermission";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import { USER_ROLE } from "../../../Constants/constant";
import ModalSkeleton from "../../../components/Modals/ModalSkeleton";
import CustomButton from "../../../components/common/Button/CustomButton";
import InputField from "../../../components/common/Input/Input";
import ConfirmationModal from "../../../components/common/Modal/ConfirmationModal";
import PolicyCategoryDeleteConfirmContent from "./PolicyCategoryDeleteConfirmContent";

const PolicyCategoriesPage = () => {
  const { sideBarPadding } = useContext(LoginContext);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [openCreateCategoryModal, setOpenCreateCategoryModal] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [errors, setErrors] = useState({});
  const [deleteLoader, setDeleteLoader] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [categoryPagination, setCategoryPagination] = useState(emptyPaginationObj);

  const canManageCategories = roleAccess(
    [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager],
    PERMISSION_KEYS.POLICy,
    PERMISSION_KEYS_CHILDREN.POLICY.CREATE
  );

  const loadCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      const url = addFiltersAndPagination("sp/policy-categories", {
        page: categoryPagination.page,
        per_page: POLICY_CATEGORIES_LIST_LIMIT,
      });
      const res = await apiGetRequest(url);
      if (res.status === 200) {
        const { rows, paginationBlock, queryParams } =
          normalizePolicyCategoriesIndexPayload(res);
        setCategories(rows);
        setCategoryPagination(getPaginationObj(paginationBlock, queryParams));
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.log("Error fetching categories:", err);
    }
  }, [categoryPagination.page]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const resetForm = () => {
    setCategoryName("");
    setEditingCategory(null);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!categoryName || categoryName.trim().length === 0) {
      newErrors.name = "Category name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCategoryNameChange = (val) => {
    setCategoryName(val);
    if (errors?.name && val && val.trim().length > 0) {
      setErrors((prev) => {
        if (!prev?.name) return prev;
        const next = { ...prev };
        delete next.name;
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const payload = {
        name: categoryName.trim(),
        description: null,
      };

      let res;
      if (editingCategory) {
        res = await apiPutRequest(`sp/policy-categories/${editingCategory.uuid}`, payload);
      } else {
        res = await apiPostRequest("sp/policy-categories", payload);
      }

      if (res.status === 200 || res.status === 201) {
        toast.success(
          editingCategory
            ? "Category updated successfully"
            : "Category created successfully"
        );
        resetForm();
        setOpenCreateCategoryModal(false);
        loadCategories();
      }
      setIsSubmitting(false);
    } catch (err) {
      setIsSubmitting(false);
      const errorMessage =
        err.response?.data?.message || err.message || "An error occurred";
      toast.error(errorMessage);
    }
  };

  const handleEdit = (category) => {
    if (category.is_default) {
      toast.warning("Cannot edit the default category name");
      setCategoryName(category.name);
      setEditingCategory(category);
      setOpenCreateCategoryModal(true);
      return;
    }
    setCategoryName(category.name);
    setEditingCategory(category);
    setOpenCreateCategoryModal(true);
  };

  const handleDeleteClick = (category) => {
    if (category.is_default) {
      toast.warning("Cannot delete the default category");
      return;
    }
    setCategoryToDelete(category);
  };

  const executeDelete = async () => {
    if (!categoryToDelete) return;
    const category = categoryToDelete;
    try {
      setDeleteLoader(category.uuid);
      const res = await apiDeleteRequest(`sp/policy-categories/${category.uuid}`);
      if (res.status === 200) {
        toast.success("Category deleted successfully");
        setCategoryToDelete(null);
        loadCategories();
      }
      setDeleteLoader(null);
    } catch (err) {
      setDeleteLoader(null);
      const errorMessage =
        err.response?.data?.message || err.message || "An error occurred";
      toast.error(errorMessage);
    }
  };

  const modalFooter = () => {
    return (
      <CustomButton
        isDisabled={isSubmitting}
        showLoading={isSubmitting}
        clickHandler={handleSubmit}
        label={editingCategory ? "Update Category" : "Save Category"}
      />
    );
  };

  return (
    <>
      <div className='min-h-full'>
        <SideBar />
        <div
          className={`${
            sideBarPadding
              ? "lg:pl-[4rem] duration-300"
              : "lg:pl-64 duration-300"
          }  flex flex-col`}
        >
          <Header
            title='Policy Categories'
            breadcrumb={
              <Breadcrumb
                pages={SP_POLICIES_BREADCRUMB(
                  SP_POLICIES_BREADCRUMB_PAGES.POLICY_CATEGORIES
                )}
              />
            }
            handleClick={() => {
              resetForm();
              setOpenCreateCategoryModal(true);
            }}
            btnLabel='Add Category'
            icon={<BiPlus />}
            isButton={canManageCategories}
          />

          <div className='col-span-1 px-8 mt-4'>
            <div className='block bg-white border border-gray-200 rounded-lg shadow-md mb-6'>
              <div className='flex flex-col'>
                <div className='min-w-full'>
                  <div className='shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'>
                    <div className='table-wrp block'>
                      <table className='min-w-full divide-y divide-gray-300 '>
                        <thead className='bg-gray-50 sticky top-[80px] z-[2] capitalize'>
                          <tr>
                            <th
                              scope='col'
                              className='text-gray-900 py-3.5 pl-3 pr-4 text-left bg-blue-50 text-sm font-semibold'
                            >
                              Title
                            </th>
                            <th
                              scope='col'
                              className='text-gray-900 py-3.5 pl-3 pr-4 text-center bg-blue-50 text-sm font-semibold'
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className='divide-y divide-gray-200 bg-white overflow-y-auto'>
                          {isLoading ? (
                            <tr>
                              <td
                                className='w-full text-center py-4 bg-gray-50'
                                colSpan={2}
                              >
                                <CircularLoader
                                  classes='flex justify-center items-center'
                                  classloader='loader-l'
                                />
                              </td>
                            </tr>
                          ) : (
                            <>
                              {categories.length > 0 &&
                                categories.map((category) => (
                                  <tr key={category.id}>
                                    <td
                                      title={category.name}
                                      className=' px-3 py-4 text-sm text-gray-500'
                                    >
                                      {category.name}
                                    </td>
                                    <td className='relative py-4 pl-3 pr-4 text-center text-sm font-medium'>
                                      {canManageCategories ? (
                                        <div className='flex items-center justify-center gap-x-3'>
                                          {category.is_default ? (
                                            <span className='text-sm inline-flex items-center px-3 py-0.5 rounded-full font-medium bg-blue-50 text-blue-800 whitespace-nowrap'>
                                              Default
                                            </span>
                                          ) : (
                                            <>
                                              <div
                                                onClick={() => handleEdit(category)}
                                                className='icon-pencil-wrapper cursor-pointer'
                                                title='Edit'
                                              >
                                                <BiPencil className='icon-size' />
                                              </div>
                                              <div
                                                onClick={() => handleDeleteClick(category)}
                                                className='icon-delete-wrapper cursor-pointer'
                                                title='Delete'
                                                disabled={deleteLoader === category.uuid}
                                              >
                                                {deleteLoader === category.uuid ? (
                                                  <Loader isSmallView />
                                                ) : (
                                                  <HiOutlineTrash className='icon-size' />
                                                )}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      ) : (
                                        category.is_default && (
                                          <span className='text-sm inline-flex items-center px-3 py-0.5 rounded-full font-medium bg-blue-50 text-blue-800 whitespace-nowrap'>
                                            Default
                                          </span>
                                        )
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              {categories.length === 0 && (
                                <tr>
                                  <td
                                    colSpan={2}
                                    className='py-6 text-center text-red-600'
                                  >
                                    No data found!
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {!isLoading &&
              Number(categoryPagination.totalPages) > 0  && (
              <div className="px-4 py-3 border-t border-gray-200">
                <PaginationComponent
                  page={categoryPagination.page}
                  activeListLength={categories}
                  limit={POLICY_CATEGORIES_LIST_LIMIT}
                  totalList={categoryPagination.total}
                  handlePagination={(nextPage) => {
                    setCategoryPagination((prev) => ({ ...prev, page: nextPage }));
                  }}
                  totalPages={categoryPagination.totalPages}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {openCreateCategoryModal && (
        <ModalSkeleton
          modalFooter={modalFooter()}
          isOpen={openCreateCategoryModal}
          closeModal={() => {
            resetForm();
            setOpenCreateCategoryModal(false);
          }}
          cssClass='w-[50%]'
          title={editingCategory ? "Edit Category" : "Add Category"}
        >
          <div className='space-y-4'>
            <div>
              <InputField
                value={categoryName}
                name="categoryName"
                label="Category Name"
                type="text"
                handleChange={handleCategoryNameChange}
                ErrorMessage={errors.name}
                isRequired={true}
                isDisabled={editingCategory?.is_default}
              />
            </div>
          </div>
        </ModalSkeleton>
      )}

      <ConfirmationModal
        isNew={true}
        showModal={categoryToDelete !== null}
        closeModalHandler={() => setCategoryToDelete(null)}
        submitHandler={executeDelete}
        title="Delete policy category"
        heading=""
        children={
          categoryToDelete ? (
            <PolicyCategoryDeleteConfirmContent
              categoryName={categoryToDelete.name}
            />
          ) : null
        }
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Delete category"
        variantConfirmBtn="danger"
        confirmBtnLoader={
          !!categoryToDelete && deleteLoader === categoryToDelete.uuid
        }
      />
    </>
  );
};

export default PolicyCategoriesPage;

